// Захват off-screen ShareCard через html2canvas → PNG → sharePng (нативный share-sheet /
// Web Share / скачивание). Управляет loading-стейтом кнопки и тостами.
// Source of truth: docs/specs/sprint-6-sharing/stage-1-contracts.md §4.

import { useCallback, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { sharePng, type ShareResult } from '@/lib/share';

type ShareInput = { filename: string; title: string };

export function useShareChart() {
  const cardRef = useRef<HTMLDivElement>(null);
  // Синхронный lock: React state обновляется асинхронно, поэтому быстрый двойной
  // тап мог стартовать два html2canvas/share flow. ref защищает в том же тике.
  const sharingRef = useRef(false);
  const [isSharing, setIsSharing] = useState(false);
  const { toast } = useToast();

  const share = useCallback(
    async ({ filename, title }: ShareInput) => {
      const node = cardRef.current;
      if (!node || sharingRef.current) return;
      sharingRef.current = true;
      setIsSharing(true);
      try {
        // Динамический импорт — html2canvas тяжёлый, не нужен на других экранах.
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(node, {
          scale: 3, // ~360 CSS px → ~1080 px ретина (D3)
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
        });
        const blob = await canvasToPngBlob(canvas);
        const result = await sharePng(blob, filename, title);
        notify(result);
      } catch (err) {
        console.error('[share-chart] failed', err);
        toast({
          variant: 'destructive',
          title: 'Не удалось поделиться',
          description: 'Попробуйте ещё раз.',
        });
      } finally {
        sharingRef.current = false;
        setIsSharing(false);
      }
    },
    [toast],
  );

  const notify = useCallback(
    (result: ShareResult) => {
      // 'shared' — нативный/Web share-sheet сам даёт фидбек, тост не нужен.
      // 'cancelled' — юзер закрыл шит, молчим (D10).
      if (result === 'downloaded') {
        toast({ title: 'Картинка сохранена', description: 'График скачан как PNG.', variant: 'success' });
      } else if (result === 'unsupported') {
        toast({
          variant: 'destructive',
          title: 'Шеринг недоступен',
          description: 'Это устройство не поддерживает шеринг.',
        });
      }
    },
    [toast],
  );

  return { cardRef, share, isSharing };
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });
}
